// =====================================================================
// EncounterView (PME Sprint 2 / S51) — visualização read-only da consulta
// ASSINADA, num template bonito (em vez de campos de form desabilitados).
// Como a consulta assinada é imutável, aqui é só apresentação.
// =====================================================================
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Paper, Grid, Typography, Chip, Divider } from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import AssignmentIcon from "@material-ui/icons/Assignment";

import { ENCOUNTER_TYPE_LABELS } from "../../services/encounterService";

const useStyles = makeStyles((theme) => ({
  header: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    borderLeft: `5px solid ${theme.palette.success.main}`
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  title: { fontWeight: 700, display: "flex", alignItems: "center", gap: theme.spacing(1) },
  headerMeta: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginTop: 6,
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5, 2)
  },
  signedLine: {
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.success.main,
    fontSize: 13.5,
    fontWeight: 600
  },
  section: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    marginBottom: theme.spacing(1.5)
  },
  metrics: { display: "flex", gap: theme.spacing(1.5), flexWrap: "wrap" },
  metric: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    textAlign: "center",
    minWidth: 92
  },
  metricV: { fontSize: 20, fontWeight: 700, color: theme.palette.primary.main },
  metricL: {
    fontSize: 10.5,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginTop: 2
  },
  soapLabel: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.primary.main,
    fontWeight: 700
  },
  soapText: { fontSize: 15, whiteSpace: "pre-wrap", marginTop: 2 },
  soapBlock: { marginBottom: theme.spacing(2) },
  empty: { color: theme.palette.text.secondary, fontStyle: "italic" }
}));

const pad = (n) => String(n).padStart(2, "0");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Metric = ({ v, l, classes }) => {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  return (
    <div className={classes.metric}>
      <div className={classes.metricV}>{v}</div>
      <div className={classes.metricL}>{l}</div>
    </div>
  );
};

const Soap = ({ label, text, classes }) => (
  <div className={classes.soapBlock}>
    <div className={classes.soapLabel}>{label}</div>
    <div className={classes.soapText}>{text && String(text).trim() ? text : "—"}</div>
  </div>
);

const EncounterView = ({ encounter, professionals = [], appointments = [] }) => {
  const classes = useStyles();
  if (!encounter) return null;

  const profName = (() => {
    const p = professionals.find((x) => String(x.id) === String(encounter.professionalId));
    return p ? p.name : encounter.professionalId ? `#${encounter.professionalId}` : "—";
  })();

  const linkedAppt = encounter.appointmentId
    ? appointments.find((a) => String(a.id) === String(encounter.appointmentId))
    : null;

  const v = encounter.vitalSigns || {};
  const cids = Array.isArray(encounter.cidCodes) ? encounter.cidCodes : [];
  const hasVitals = ["pa", "fc", "peso", "altura", "imc", "glicemia", "temp", "estadoNutricional"].some(
    (k) => v[k] && String(v[k]).trim() !== ""
  );

  return (
    <>
      {/* ---------- Cabeçalho ---------- */}
      <Paper className={classes.header} variant="outlined">
        <div className={classes.titleRow}>
          <Typography variant="h6" className={classes.title}>
            <AssignmentIcon color="primary" /> Consulta · {fmtDateTime(encounter.encounterDate)}
          </Typography>
          <Chip
            size="small"
            icon={<LockIcon style={{ fontSize: 14 }} />}
            label="Assinada"
            style={{ background: "#d1fae5", color: "#059669" }}
          />
        </div>
        <div className={classes.headerMeta}>
          <span><strong>Tipo:</strong> {ENCOUNTER_TYPE_LABELS[encounter.encounterType] || encounter.encounterType || "—"}</span>
          <span><strong>Profissional:</strong> {profName}</span>
          {linkedAppt ? (
            <span><strong>Agendamento:</strong> {fmtDateTime(linkedAppt.datetimeStart)}{linkedAppt.procedureType ? " · " + linkedAppt.procedureType : ""}</span>
          ) : null}
        </div>
        <div className={classes.signedLine}>
          <LockIcon style={{ fontSize: 15 }} /> Registro do prontuário assinado em {fmtDateTime(encounter.signedAt)} (não pode mais ser editado)
        </div>
      </Paper>

      {/* ---------- Sinais vitais ---------- */}
      {hasVitals && (
        <Paper className={classes.section} variant="outlined">
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            <FavoriteBorderIcon fontSize="small" /> Sinais vitais
          </Typography>
          <div className={classes.metrics}>
            <Metric v={v.pa} l="PA (mmHg)" classes={classes} />
            <Metric v={v.fc} l="FC (bpm)" classes={classes} />
            <Metric v={v.peso} l="Peso (kg)" classes={classes} />
            <Metric v={v.altura} l="Altura (cm)" classes={classes} />
            <Metric v={v.imc} l="IMC" classes={classes} />
            <Metric v={v.temp} l="Temp (°C)" classes={classes} />
            <Metric v={v.glicemia} l="Glicemia" classes={classes} />
          </div>
          {v.estadoNutricional && String(v.estadoNutricional).trim() ? (
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span className={classes.metricL}>Estado nutricional</span>
              <Chip size="small" label={v.estadoNutricional} />
            </div>
          ) : null}
        </Paper>
      )}

      {/* ---------- SOAP ---------- */}
      <Paper className={classes.section} variant="outlined">
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          <AssignmentIcon fontSize="small" /> Evolução (SOAP)
        </Typography>
        <Soap label="S — Subjetivo" text={encounter.subjective} classes={classes} />
        <Soap label="O — Objetivo" text={encounter.objective} classes={classes} />
        <Soap label="A — Avaliação" text={encounter.assessment} classes={classes} />
        <Soap label="P — Plano / Conduta" text={encounter.plan} classes={classes} />

        {cids.length > 0 && (
          <>
            <Divider style={{ margin: "8px 0 12px" }} />
            <div className={classes.soapLabel}>CID-10</div>
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {cids.map((c, i) => (
                <Chip key={i} label={c} size="small" />
              ))}
            </div>
          </>
        )}
      </Paper>
    </>
  );
};

export default EncounterView;
